import { toast } from '@kalos/ui';
import { RpcError } from '@protobuf-ts/runtime-rpc';
import { QueryCache, QueryClient } from '@tanstack/react-query';

import { outsideAuthSignout, outsideAuthUpdateToken } from '../App';
import { CopyButton } from '../components/CopyButton';

const REFRESH_ATTEMPTS = 4;
const REFRESH_RETRY_DELAY_MS = 1000;

// Only the server refusing the refresh signs out. A refresh that did not get through, such as
// a dropped connection on a phone that just woke up, is retried with backoff and the session
// is kept either way; the timer in AuthProvider still signs out after three failed refreshes.
const recoverAuth = async () => {
  for (let attempt = 1; ; attempt++) {
    try {
      await outsideAuthUpdateToken?.();
      return true;
    } catch (err) {
      if (err instanceof RpcError && err.code === 'UNAUTHENTICATED') {
        console.error('Token refresh refused by the server, signing out', err);
        outsideAuthSignout?.();
        return false;
      }
      if (attempt >= REFRESH_ATTEMPTS) {
        console.error(`Token refresh failed ${attempt} times, keeping the session`, err);
        return false;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, REFRESH_RETRY_DELAY_MS * 2 ** (attempt - 1)),
      );
    }
  }
};

// Every request that fails with UNAUTHENTICATED lands here at once; they share one recovery.
let recovery: Promise<boolean> | null = null;

const handleAuthIssue = () => {
  if (!recovery) {
    recovery = recoverAuth().finally(() => {
      recovery = null;
    });
  }
  return recovery;
};

// gRPC codes that are the server's final answer (not a hiccup worth retrying).
// UNAUTHENTICATED is left to the cache's token refresh below.
const FINAL_CODES = ['UNIMPLEMENTED', 'PERMISSION_DENIED', 'NOT_FOUND', 'INVALID_ARGUMENT'];

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry(failureCount, error) {
        // A final answer from the server won't change on a retry: show the error now.
        if (error instanceof RpcError && FINAL_CODES.includes(error.code)) return false;
        const shouldRetry = failureCount <= 1;
        return shouldRetry;
      },
    },
    mutations: {
      async onError(error) {
        if (error instanceof RpcError && error.code === 'UNAUTHENTICATED') {
          console.log('Handling unauthenticated error - signing off', error);
          await handleAuthIssue();
        } else {
          console.log('Unexpected Error from mutation', { error });
        }
      },
    },
  },
  queryCache: new QueryCache({
    async onError(error, query) {
      console.log('onError queryCache', { error, query });
      if (error instanceof RpcError && error.code === 'UNAUTHENTICATED') {
        if (await handleAuthIssue()) {
          query.fetch().catch(console.log);
        }
      } else {
        // meta.silentError skips the toast. It is for reads whose failure already has a safe,
        // visible fallback (feature flags read as off; the flag history drawer shows its own
        // error inline), so a failed background read doesn't tell everyone to call engineering.
        // Don't set it on a query where a silent failure would hide real breakage.
        if (query.meta?.silentError) return;
        if (error instanceof RpcError) {
          // valid cases
          if (error.code === 'NOT_FOUND') return;

          console.log('Unexpected Error from query', { error, query });
          const location = [error.serviceName, error.methodName].filter(Boolean).join('.');
          const fullError = [location, error.message, error.code, error.cause, error.stack]
            .filter(Boolean)
            .join(' - ');
          toast({
            title: 'Unexpected request error',
            description: (
              <div className="space-y-2 text-xs">
                {location && <p className="font-bold ">{location}</p>}
                <p className="text-sm font-bold">{error.message}</p>
                <p className="text-sm font-bold">Code: {error.code}</p>
                <p>Please contact engineering support with this message</p>
                <CopyButton text={fullError} />
              </div>
            ),
            variant: 'destructive',
            duration: 15_000,
          });
        }
      }
    },
  }),
});
